import React, { useState, useEffect, useCallback } from 'react';
import { ColumnsType } from 'antd/es/table';
import { Input, DatePicker, Button, Badge, Select } from 'antd';
import { BaseTable } from '@app/components/common/BaseTable/BaseTable';
import { notificationController } from 'controllers/notificationController';
import moment from 'moment';
import { getAllDeliveriesOfSubscriptionAction, getAllOrdersAction, getAllSubscriptionAction } from '@app/store/slices/userSlice';
import { useAppDispatch } from '@app/hooks/reduxHooks';
import styled from 'styled-components';
import { BaseSpace } from '@app/components/common/BaseSpace/BaseSpace';
import { BaseButton } from '@app/components/common/BaseButton/BaseButton';
import { useNavigate, useParams } from 'react-router-dom';
import config from '@app/config';

const { RangePicker } = DatePicker;
const { Option } = Select;

export const BasicTableSubscriptionDeliveries: React.FC = () => {
  const [filters, setFilters] = useState({
    quantity: null,
    deliveryDateRange: [],
    deliveryStatus: '',
  });
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [tableData, setTableData] = useState({ data: [], loading: false, total: 0 });
  const dispatch = useAppDispatch();
  let navigate = useNavigate();
  const { id } = useParams();
  // Fetch data with updated filters and pagination
  const fetchData = useCallback(() => {
    setTableData((prev) => ({ ...prev, loading: true }));

    const params = {
      ...filters,
      deliveryDateRange: filters.deliveryDateRange.length
        ? [
          moment(filters.deliveryDateRange[0]).toISOString(),
          moment(filters.deliveryDateRange[1]).toISOString(),
        ]
        : [],
      current: pagination.current,
      pageSize: pagination.pageSize,
    };

    dispatch(getAllDeliveriesOfSubscriptionAction({ id, params })).unwrap()
      .then((response) => {

        const sortedData = response.data.sort((a, b) => {
          const dateA = new Date(a.date).getTime();
          const dateB = new Date(b.date).getTime();
          return dateA - dateB; // Ascending order
        });

        setTableData({
          data: sortedData,
          loading: false,
          total: response.total,
        });
      })
      .catch((error) => {
        notificationController.error({ message: error.message || 'Failed to fetch data' });
        setTableData((prev) => ({ ...prev, loading: false }));
      });
  }, [pagination, dispatch]);



  useEffect(() => {
    function hideError(e) {
      if (e.message === 'ResizeObserver loop completed with undelivered notifications.') {
        const resizeObserverErrDiv = document.getElementById(
          'webpack-dev-server-client-overlay-div'
        );
        const resizeObserverErr = document.getElementById(
          'webpack-dev-server-client-overlay'
        );
        if (resizeObserverErr) {
          resizeObserverErr.setAttribute('style', 'display: none');
        }
        if (resizeObserverErrDiv) {
          resizeObserverErrDiv.setAttribute('style', 'display: none');
        }
      }
    }

    window.addEventListener('error', hideError)
    return () => {
      window.addEventListener('error', hideError)
    }
  }, [])

  // Trigger fetch when filters or pagination change
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle filter changes
  const handleFilterChange = (filterName: string, value: any) => {
    setFilters((prev) => ({ ...prev, [filterName]: value }));
  };

  // Handle table pagination change
  const handleTableChange = (paginationInfo) => {
    setPagination({ current: paginationInfo.current, pageSize: paginationInfo.pageSize });
  };

  const columns: ColumnsType<any> = [


    {
      title: 'Order ID',
      dataIndex: 'orderId',
      render: (text) => <span>{text || 'Not available'}</span>,
    },
    {
      title: 'Delivery Date',
      dataIndex: 'date',
      render: (text, record) => (
        <span>{record.date ? moment(record?.date).format('DD-MMM-YYYY') : ""}</span>
      )

    },


    {
      title: 'Quantity',
      dataIndex: 'quantity',
      render: (text) => <span>{text || 'Not available'}</span>,
    },

    {
      title: 'Proof (if available)',
      dataIndex: 'file_info',
      render: (text, record) => <span>{record.file_info ? <span style={{ cursor: 'pointer', textDecoration: 'underline' }} onClick={() => {
        window.open(config.API1Url + '/' + record?.file_info?.filePath, '_blank');
      }}>View File</span> : 'Not Available Yet'}</span>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (text, record) => <span>{text}</span>,

    },

  ];

  return (
    <>
      <Button
        style={{ marginBottom: '20px' }}
        onClick={() => {
          setTableData((prev) => ({ ...prev, loading: true }));
          setFilters({
            clientName: '',
            deliveryDateRange: [],
            deliveryStatus: ''
          });

          setTimeout(() => {
            setPagination({ current: 1, pageSize: 10 });
            // Explicitly clear filter dropdowns
            document.querySelectorAll('.ant-table-filter-dropdown').forEach((dropdown) => {
              const inputs = dropdown.querySelectorAll('input, .ant-select-clear');
              inputs.forEach((input) => {
                if (input.tagName === 'INPUT') {
                  (input as HTMLInputElement).value = '';
                } else if (input.classList.contains('ant-select-clear')) {
                  (input as HTMLElement).click();
                }
              });
            });

          }, 1000);
        }}>Clear Filters</Button>
      {!tableData.loading &&
        <BaseTable
          columns={columns}
          dataSource={tableData.data}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: tableData.total,
          }}
          loading={tableData.loading}
          onChange={handleTableChange}
          scroll={{ x: 800 }}
          bordered
        />
      }
    </>
  );
};
